"use client"

import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { exchangeActions, peerActions } from "../../../redux/actions"
import { allRemoteSettingsSelect, exchangeSelect, peerSelect, userSelect } from "../../../redux/selectors"
import { formatDateTime, guessAdminRoute, money, notify } from "../../../utility/Helper1"
import Countdown from "react-countdown"

// Lucide icons
import {
    Clock,
    AlertTriangle,
    Check,
    Upload,
    Eye,
    AlertCircle,
    ArrowRight,
    BarChart2,
    ArrowDownLeft,
    ArrowUpRight,
    CheckCircle,
    Activity,
    Wallet,
    CreditCard,
    User,
    Phone,
    BanknoteIcon as Bank,
} from "lucide-react"

// MUI components
import {
    Button,
    Chip,
    Avatar,
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    IconButton,
    Badge,
    Tooltip,
    Drawer,
} from "@mui/material"
import WrapUpP2p from "./layouts/WrapUpP2p"

const Diva = ({children}) => {
    return <div>
        {children}
    </div>
}

const P2POrderDisplay = ({singly}) => {
    // State management
    const [activeTab, setActiveTab] = useState("all")
    const [uploadingProof, setUploadingProof] = useState(false)
    const [reportingProof, setReportingProof] = useState(false)
    const [confirmingProof, setConfirmingProof] = useState(false)
    const [focusedPeer, setFocusedPeer] = useState(null)
    const [marketLoading, setMarketLoading] = useState(false)
    const [targetDate, setTargetDate] = useState(new Date(Date.now() + 2 * 60 * 1000))
    const [resetKey, setResetKey] = useState(0)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [selectedOrder, setSelectedOrder] = useState(null)

    // Redux hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    // Selectors
    const site_settings = useSelector(allRemoteSettingsSelect)
    const settings = site_settings?.operational_settings
    const peerRoot = useSelector(peerSelect)
    const exchangeRoot = useSelector(exchangeSelect)
    const userData = useSelector(userSelect)
    const [peerData, setPeerData] = useState(peerRoot?.filter((el) => ["YES", "NO"].includes(el.status)))

    // Current user ID
    const currentUserAccountId = userData?.accountId

    // Filtered data
    const incomingPayments = peerData?.filter((order) => order?.gherAccountId == currentUserAccountId)
    const outgoingPayments = peerData?.filter((order) => order?.pherAccountId == currentUserAccountId)
    const completedPayments = peerRoot?.filter((order) => order?.status == "PAID")


    useEffect(() => {
        const loadTransData = peerRoot?.filter((el) => ["YES", "NO"].includes(el.status));
        setPeerData(loadTransData)
    }, [peerRoot])


    // Filter orders based on active tab
    const filteredOrders =
        activeTab === "all"
            ? (peerData ?? [])
            : activeTab === "incoming"
                ? incomingPayments
                : activeTab === "outgoing"
                    ? outgoingPayments
                    : completedPayments

    // Load data on mount
    useEffect(() => {
        onLoadPeer()
        onLoadPH()
    }, [])

    const onLoadPeer = () => {
        dispatch(peerActions.onGet("p2p/load/peer", (response) => { }))
    }

    const onLoadPH = () => {
        dispatch(exchangeActions.onGetExchange("p2p/load/all", (response) => { }))
    }

    // Handle file selection and upload
    const handleFileUpload = (orderId) => {
        document.getElementById(`file-${orderId}`).click()
    }

    const handleFileChange = (e, orderId) => {
        setUploadingProof(true)

        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0]
            dispatch(
                peerActions.onPost({ image: file }, `p2p/payment/upload/${orderId}`, (response) => {
                    setUploadingProof(false)
                    notify(response.msg, response.code)
                }),
            )
        }
    }

    const reportTransaction = (orderId) => {
        setReportingProof(true)

        dispatch(
            peerActions.onGet(`p2p/payment/report/${orderId}`, (response) => {
                setReportingProof(false)
                notify(response.msg, response.code)
            }),
        )
    }

    const handleMarkAsPaid = (orderId) => {
        setConfirmingProof(true)

        dispatch(
            peerActions.onPost({paid: 1}, `p2p/payment/confirm/${orderId}`, (response) => {
                setConfirmingProof(false)
                if(response.code == 1){
                    closeOrderDetails()
                }
                notify(response.msg, response.code)
            }),
        )
    }

    const handleReset = () => {
        setTargetDate(new Date(Date.now() + 2 * 60 * 1000))
        setResetKey((prev) => prev + 1)
    }

    const handleGoToMarket = () => {
        const allAccountPH = exchangeRoot?.reduce((sum, item) => sum + (Number(item.ph) || 0), 0)

        if (allAccountPH <= 0) {
            notify("You don't have any peerable bid. Please bid.", 0)
            return
        }

        if (marketLoading) {
            notify("You have an active ongoing request.", 0)
            return
        }

        setMarketLoading(true)

        dispatch(
            peerActions.onPost({ loadMarket: true }, `p2p/market/time`, (response) => {
                setMarketLoading(false)
                notify(response.msg, response.code)
            }),
        )
    }

    const handleAdd = () => {
        navigate("/projects/new")
    }

    const openOrderDetails = (order) => {
        setSelectedOrder(order)
        setDrawerOpen(true)
    }

    const closeOrderDetails = () => {
        setDrawerOpen(false)
        setTimeout(() => setSelectedOrder(null), 300)
    }

    // Calculate market progress percentage
    const calculateMarketProgress = () => {
        if (!settings?.p2p_markettimeStart || !settings?.p2p_markettimeEnds) return 0

        const start = new Date(settings.p2p_markettimeStart).getTime()
        const end = new Date(settings.p2p_markettimeEnds).getTime()
        const now = Date.now()

        if (now < start) return 0
        if (now > end) return 100

        return ((now - start) / (end - start)) * 100
    }

    // Tabs configuration
    const tabs = [
        { id: "all", label: "All Active", count: peerData?.length ?? 0, icon: <BarChart2 className="w-4 h-4" /> },
        {
            id: "incoming",
            label: "Incoming",
            count: incomingPayments?.length ?? 0,
            icon: <ArrowDownLeft className="w-4 h-4" />,
        },
        {
            id: "outgoing",
            label: "Outgoing",
            count: outgoingPayments?.length ?? 0,
            icon: <ArrowUpRight className="w-4 h-4" />,
        },
        {
            id: "completed",
            label: "Completed",
            count: completedPayments?.length ?? 0,
            icon: <CheckCircle className="w-4 h-4" />,
        },
    ]


    const payRegFee = (e, orderId) => {

        dispatch(
            peerActions.onPost({ paynow: 1 }, `p2p/reg/fee`, (response) => {
                // notify(response.msg, response.code)
            }),
        )
    }


    useEffect(() => {
        payRegFee()
    }, [])

    const BorderLine = singly?Diva:WrapUpP2p

    return (<BorderLine>
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white">
            {/* Market Progress Bar */}

            <div className="max-w-6xl mx-auto px-4 py-8">
                {/* Header */}

                {userData?.fee == 1 && <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-cyan-300 to-purple-300 bg-clip-text text-transparent">
                        Successful Bid Orders
                    </h1>
                    <p className="text-gray-400">Manage your peer-to-peer transactions securely</p>
                </div>}

                {userData?.fee == 0 && <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-cyan-300 to-purple-300 bg-clip-text text-transparent">
                        Reg Fee
                    </h1>

                    {(peerData?.length ?? 0) == 0 && <Button
                        onClick={() => window.location.reload()}
                        variant="contained"
                        fullWidth
                        className={`text-sm py-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:shadow-cyan-500/20`}
                    >
                        Pay Now
                    </Button>}

                </div>}

                {/* Stats Cards */}
                {1 > 2 && <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    {/* Active Orders */}
                    <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 rounded-xl p-6 border border-cyan-800/50 shadow-lg backdrop-blur-sm relative overflow-hidden">
                        <div className="absolute inset-0 bg-cyan-500/5"></div>
                        <div className="relative z-10">
                            <div className="flex items-center mb-2">
                                <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center mr-3">
                                    <Activity className="text-cyan-400" size={20} />
                                </div>
                                <span className="text-cyan-100 font-medium">Active Orders</span>
                            </div>
                            <div className="text-3xl font-bold text-white">
                                {peerData?.filter((order) => ["NO", "YES", "REJECTED"].includes(order?.status)).length}
                            </div>
                        </div>
                    </div>

                    {/* Pending Confirmation */}
                    <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/30 rounded-xl p-6 border border-amber-800/50 shadow-lg backdrop-blur-sm relative overflow-hidden">
                        <div className="absolute inset-0 bg-amber-500/5"></div>
                        <div className="relative z-10">
                            <div className="flex items-center mb-2">
                                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center mr-3">
                                    <Clock className="text-amber-400" size={20} />
                                </div>
                                <span className="text-amber-100 font-medium">Pending Confirmation</span>
                            </div>
                            <div className="text-3xl font-bold text-white">
                                {peerData?.filter((order) => order?.status === "YES").length}
                            </div>
                        </div>
                    </div>

                    {/* Reported */}
                    <div className="bg-gradient-to-br from-rose-900/30 to-red-900/30 rounded-xl p-6 border border-rose-800/50 shadow-lg backdrop-blur-sm relative overflow-hidden">
                        <div className="absolute inset-0 bg-rose-500/5"></div>
                        <div className="relative z-10">
                            <div className="flex items-center mb-2">
                                <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center mr-3">
                                    <AlertTriangle className="text-rose-400" size={20} />
                                </div>
                                <span className="text-rose-100 font-medium">Reported Issues</span>
                            </div>
                            <div className="text-3xl font-bold text-white">
                                {peerData?.filter((order) => order?.reported == "1").length}
                            </div>
                        </div>
                    </div>
                </div>}

                {/* Tabs */}
                {1 > 2 && <div className="mb-8">
                    <div className="flex flex-wrap gap-2">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${activeTab === tab.id
                                    ? "bg-gradient-to-r from-purple-600 to-cyan-600 text-white shadow-lg shadow-purple-500/20"
                                    : "bg-gray-800/50 text-gray-400 hover:bg-gray-800/80 border border-gray-700"
                                    }`}
                            >
                                {tab.icon}
                                <span>{tab.label}</span>
                                <Badge
                                    badgeContent={tab.count}
                                    color={activeTab === tab.id ? "secondary" : "default"}
                                    className="ml-1"
                                    sx={{
                                        "& .MuiBadge-badge": {
                                            backgroundColor: activeTab === tab.id ? "#a855f7" : "#6b7280",
                                            color: "white",
                                        },
                                    }}
                                />
                            </button>
                        ))}
                    </div>
                </div>}

                {/* Orders List */}
                {filteredOrders.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {filteredOrders.map((order) => {
                            const isPayer = order?.pherAccountId === currentUserAccountId
                            const isReceiver = order?.gherAccountId === currentUserAccountId

                            // Status styling
                            let statusColor = "gray"
                            let statusBg = "bg-gray-800"
                            let statusBorder = "border-gray-700"

                            if (order?.status === "PAID") {
                                statusColor = "green"
                                statusBg = "bg-green-900/30"
                                statusBorder = "border-green-700/50"
                            } else if (order?.status === "YES") {
                                statusColor = "amber"
                                statusBg = "bg-amber-900/30"
                                statusBorder = "border-amber-700/50"
                            } else if (order?.status === "NO") {
                                statusColor = "blue"
                                statusBg = "bg-blue-900/30"
                                statusBorder = "border-blue-700/50"
                            }

                            const blockingtimer = Number(order?.timestamp) + 60 * 60 * Number(order?.paywindow)

                            return (
                                <div
                                    key={order?.id}
                                    className={`${statusBg} rounded-xl border ${statusBorder} shadow-lg hover:shadow-${statusColor}-500/10 transition-all overflow-hidden relative cursor-pointer`}
                                    onClick={() => openOrderDetails(order)}
                                >
                                    {/* Glow effects */}
                                    <div className="absolute -top-20 -left-20 w-40 h-40 bg-indigo-500 rounded-full opacity-10 blur-3xl"></div>
                                    <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-purple-500 rounded-full opacity-10 blur-3xl"></div>

                                    {/* Order Header */}
                                    <div className="bg-gradient-to-r from-gray-800/80 to-gray-900/80 backdrop-blur-sm px-4 py-3 border-b border-gray-700/50">
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-2">
                                                <span className={`w-2 h-2 rounded-full bg-${statusColor}-400`}></span>
                                                {order?.utility == 1 && <span className="text-sm font-medium text-gray-300">Reg. Fee</span>}
                                                {order?.utility == 0 && <span className="text-sm font-medium text-gray-300">Peered Order</span>}
                                                <span
                                                    className={`text-xs px-2 py-0.5 rounded-full bg-${statusColor}-900/50 text-${statusColor}-300 border border-${statusColor}-700/50`}
                                                >
                                                    {order?.status === "PAID"
                                                        ? "Completed"
                                                        : order?.status === "YES"
                                                            ? "Proof Uploaded"
                                                            : order?.status === "NO"
                                                                ? "Pending Payment"
                                                                : order?.status}
                                                </span>
                                            </div>
                                            {/* <span className="text-xs text-gray-500">{formatDateTime(Number(order?.timestamp))}</span> */}
                                        </div>
                                    </div>

                                    {/* Order Content */}
                                    <div className="p-4">
                                        {/* Amount */}
                                        <div className="mb-4">
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-400 text-sm">Amount</span>
                                                <span className="text-xl font-bold bg-gradient-to-r from-cyan-300 to-purple-300 bg-clip-text text-transparent">
                                                    {money(order?.donate, order?.motherType)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center mt-1">
                                                <span className="text-xs text-gray-500">Click To View Payment Detail</span>
                                            </div>
                                        </div>

                                        {/* Transaction Flow */}
                                        <div className="flex items-center justify-between p-3 rounded-lg bg-gray-800/50 border border-gray-700/50 mb-3">
                                            {/* Sender */}
                                            <div className="text-center">
                                                <Avatar
                                                    src={`${guessAdminRoute()}/assets/uploads/avatar/${order?.pherImage}`}
                                                    className="mx-auto mb-1 w-8 h-8 border-2 border-cyan-600"
                                                    sx={{ bgcolor: "#0891b2" }}
                                                >
                                                    {order?.pherFirstname?.[0]}
                                                    {order?.pherSurname?.[0]}
                                                </Avatar>
                                                <div className="text-xs text-gray-300">@{order?.pherUsername}</div>
                                                <div className="text-[10px] bg-cyan-900/30 border border-cyan-800/50 rounded-full px-2 py-0.5 text-cyan-300 mt-1">
                                                    Payer
                                                </div>
                                            </div>

                                            {/* Arrow */}
                                            <div className="flex flex-col items-center text-[10px] px-2 py-0.5 text-cyan-300 mt-1">
                                                Paying
                                            </div>

                                            {/* Receiver */}
                                            <div className="text-center">
                                                <Avatar
                                                    src={`${guessAdminRoute()}/assets/uploads/avatar/${order?.gherImage}`}
                                                    className="mx-auto mb-1 w-8 h-8 border-2 border-purple-600"
                                                    sx={{ bgcolor: "#9333ea" }}
                                                >
                                                    {order?.gherFirstname?.[0]}
                                                    {order?.gherSurname?.[0]}
                                                </Avatar>
                                                <div className="text-xs text-gray-300">@{order?.gherUsername}</div>
                                                <div className="text-[10px] bg-purple-900/30 border border-purple-800/50 rounded-full px-2 py-0.5 text-purple-300 mt-1">
                                                    Earner
                                                </div>
                                            </div>
                                        </div>

                                        {/* Timer and Actions */}
                                        {blockingtimer && (
                                            <div className="bg-indigo-900/20 border border-indigo-800/30 rounded-lg p-2 mb-3">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-xs text-indigo-300">
                                                        <Clock className="inline w-3 h-3 mr-1" /> Time Remaining:
                                                    </span>
                                                    <span className="text-xs font-mono text-white">
                                                        <Countdown
                                                            date={new Date(Number(blockingtimer) * 1000)}
                                                            renderer={({ hours, minutes, seconds, completed }) => {
                                                                if (completed) {
                                                                    return <span className="text-rose-300">Payment timeout</span>
                                                                } else {
                                                                    return (
                                                                        <span className="bg-indigo-900/50 px-2 py-0.5 rounded border border-indigo-700/30">
                                                                            {String(hours).padStart(2, "0")}:{String(minutes).padStart(2, "0")}:
                                                                            {String(seconds).padStart(2, "0")}
                                                                        </span>
                                                                    )
                                                                }
                                                            }}
                                                        />
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Status Info */}
                                        <div className="flex justify-between items-center text-xs text-gray-400 mb-3">
                                            <div>
                                                <span className={isPayer ? "text-cyan-400" : "text-purple-400"}>
                                                    {isPayer ? "You are paying" : "You are receiving"}
                                                </span>
                                            </div>
                                            <div>
                                                {order?.image && order?.image != "" && order?.image != 0 && (
                                                    <span className="flex items-center text-green-400">
                                                        <Check className="w-3 h-3 mr-1" />
                                                        Proof available
                                                    </span>
                                                )}
                                                {order?.reported == "1" && (
                                                    <span className="flex items-center text-rose-400">
                                                        <AlertTriangle className="w-3 h-3 mr-1" />
                                                        Reported
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Action Button */}
                                        <div className="flex justify-center mt-2">
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                className={`text-sm py-1 ${isPayer && order?.status == "NO"
                                                    ? "bg-gradient-to-r from-cyan-600 to-blue-600 hover:shadow-cyan-500/20"
                                                    : isReceiver && order?.status == "YES"
                                                        ? "bg-gradient-to-r from-emerald-600 to-green-600 hover:shadow-emerald-500/20"
                                                        : "bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-purple-500/20"
                                                    }`}
                                            >
                                                {isPayer && order?.status == "NO" && "Upload Payment Proof"}
                                                {isPayer && order?.status == "YES" && "Waiting for confirmation"}
                                                {isReceiver && ['YES', 'NO']?.includes(order?.status) && "Confirm Payment"}
                                                {order?.status == "PAID" && "View Details"}
                                                {isReceiver && order?.status == "NO" && "Waiting for payment"}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <div className="rounded-xl bg-gray-800/30 border border-gray-700/50 p-8 text-center shadow-lg">
                        <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-4">
                            <AlertCircle size={32} className="text-gray-400" />
                        </div>
                        <Typography variant="h6" className="text-gray-300 mb-2">
                            No {activeTab !== "all" ? activeTab : ""} Orders Found
                        </Typography>
                        <Typography variant="body2" className="text-gray-500">
                            There are no {activeTab === "all" ? "" : activeTab + " "}
                            orders available at the moment.
                        </Typography>
                    </div>
                )}
            </div>

            {/* Order Details Drawer */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={closeOrderDetails}
                sx={{
                    "& .MuiDrawer-paper": {
                        width: { xs: "100%", sm: "450px" },
                        backgroundColor: "#0f172a",
                        backgroundImage: "linear-gradient(to bottom right, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.8))",
                        color: "white",
                        borderLeft: "1px solid rgba(76, 29, 149, 0.3)",
                    },
                }}
            >
                {selectedOrder && (
                    <Box className="h-full overflow-y-auto">
                        {/* Header */}
                        <Box className="sticky top-0 z-10 bg-gray-900/80 backdrop-blur-sm border-b border-gray-800 p-4">
                            <div className="flex justify-between items-center">
                                <Typography variant="h6" className="text-white font-bold">
                                    Transaction Detail
                                </Typography>
                                <IconButton onClick={closeOrderDetails} className="text-white hover:text-white">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        className="w-6 h-6"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </IconButton>
                            </div>
                        </Box>

                        {/* Content */}
                        <Box className="p-4 relative">
                            {/* Glow effects */}
                            <div className="absolute top-40 left-0 w-40 h-40 bg-purple-500 rounded-full opacity-5 blur-3xl"></div>
                            <div className="absolute top-80 right-0 w-40 h-40 bg-cyan-500 rounded-full opacity-5 blur-3xl"></div>

                            {/* Amount Section */}
                            <div className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 rounded-xl p-4 mb-6 border border-purple-800/30 relative overflow-hidden">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-400">Amount</span>
                                    <span className="text-2xl font-bold bg-gradient-to-r from-purple-300 to-cyan-300 bg-clip-text text-transparent">
                                        {money(selectedOrder?.donate, selectedOrder?.motherType)}
                                    </span>
                                </div>
                                <div className="flex justify-between mt-1">
                                    <span className="text-sm text-gray-500">
                                        {selectedOrder?.autoConfirm == 1 ? "Crypto Payment" : "Fiat Payment"}
                                    </span>
                                    <span className="text-sm text-gray-500">Transaction #{selectedOrder?.transid}</span>
                                </div>
                            </div>

                            {/* Status Section */}
                            <div className="bg-gradient-to-r from-gray-900/40 to-gray-800/40 rounded-xl p-4 mb-6 border border-gray-700/30">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-gray-400">Status</span>
                                    <Chip
                                        label={
                                            selectedOrder?.status === "PAID"
                                                ? "Completed"
                                                : selectedOrder?.status === "YES"
                                                    ? "Proof Uploaded"
                                                    : selectedOrder?.status === "NO"
                                                        ? "Pending Payment"
                                                        : selectedOrder?.status
                                        }
                                        size="small"
                                        sx={{
                                            backgroundColor:
                                                selectedOrder?.status === "PAID"
                                                    ? "rgba(16, 185, 129, 0.2)"
                                                    : selectedOrder?.status === "YES"
                                                        ? "rgba(245, 158, 11, 0.2)"
                                                        : selectedOrder?.status === "NO"
                                                            ? "rgba(59, 130, 246, 0.2)"
                                                            : "rgba(107, 114, 128, 0.2)",
                                            color:
                                                selectedOrder?.status === "PAID"
                                                    ? "rgb(16, 185, 129)"
                                                    : selectedOrder?.status === "YES"
                                                        ? "rgb(245, 158, 11)"
                                                        : selectedOrder?.status === "NO"
                                                            ? "rgb(59, 130, 246)"
                                                            : "rgb(156, 163, 175)",
                                            border:
                                                selectedOrder?.status === "PAID"
                                                    ? "1px solid rgba(16, 185, 129, 0.3)"
                                                    : selectedOrder?.status === "YES"
                                                        ? "1px solid rgba(245, 158, 11, 0.3)"
                                                        : selectedOrder?.status === "NO"
                                                            ? "1px solid rgba(59, 130, 246, 0.3)"
                                                            : "1px solid rgba(107, 114, 128, 0.3)",
                                        }}
                                    />
                                </div>

                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-500">Peered on</span>
                                    <span className="text-sm text-gray-300">{formatDateTime(Number(selectedOrder?.timestamp))}</span>
                                </div>

                                {selectedOrder?.status === "YES" && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-500">Payment Uploaded</span>
                                        <span className="text-sm text-gray-300">{formatDateTime(Number(selectedOrder?.paymentTime))}</span>
                                    </div>
                                )}

                                {/* Countdown Timer */}
                                {selectedOrder?.status !== "PAID" && (
                                    <div className="mt-3 pt-3 border-t border-gray-700/30">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm text-gray-400 flex items-center">
                                                <Clock className="w-4 h-4 mr-1 text-indigo-400" />
                                                Payment Window
                                            </span>
                                            <span className="text-sm text-gray-300">{selectedOrder?.paywindow} hours</span>
                                        </div>

                                        {selectedOrder?.status === "NO" && (
                                            <div className="bg-indigo-900/20 border border-indigo-800/30 rounded-lg p-2 mt-2">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-xs text-indigo-300">Time Remaining:</span>
                                                    <span className="text-xs font-mono text-white">
                                                        <Countdown
                                                            date={
                                                                new Date(
                                                                    (Number(selectedOrder?.timestamp) + (60 * 60 * Number(selectedOrder?.paywindow))) * 1000,
                                                                )
                                                            }
                                                            renderer={({ hours, minutes, seconds, completed }) => {
                                                                if (completed) {
                                                                    return <span className="text-rose-300">Payment timeout</span>
                                                                } else {
                                                                    return (
                                                                        <span className="bg-indigo-900/50 px-2 py-0.5 rounded border border-indigo-700/30">
                                                                            {String(hours).padStart(2, "0")}:{String(minutes).padStart(2, "0")}:
                                                                            {String(seconds).padStart(2, "0")}
                                                                        </span>
                                                                    )
                                                                }
                                                            }}
                                                        />
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Transaction Parties */}
                            <div className="bg-gradient-to-r from-gray-900/40 to-gray-800/40 rounded-xl p-4 mb-6 border border-gray-700/30">
                                <h3 className="text-lg font-semibold text-white mb-4">Transaction Parties</h3>

                                {/* Payer Details */}
                                <div className="mb-4 pb-4 border-b border-gray-700/30">
                                    <div className="flex items-center mb-3">
                                        <Avatar
                                            src={`${guessAdminRoute()}/assets/uploads/avatar/${selectedOrder?.pherImage}`}
                                            className="w-10 h-10 border-2 border-cyan-600 mr-3"
                                            sx={{ bgcolor: "#0891b2" }}
                                        >
                                            {selectedOrder?.pherFirstname?.[0]}
                                            {selectedOrder?.pherSurname?.[0]}
                                        </Avatar>
                                        <div>
                                            <div className="text-white font-medium">
                                                {selectedOrder?.pherFirstname} {selectedOrder?.pherSurname}
                                            </div>
                                            <div className="text-xs text-gray-400 flex items-center">
                                                <User className="w-3 h-3 mr-1" />@{selectedOrder?.pherUsername}
                                                <span className="mx-2">•</span>
                                                <span className="bg-cyan-900/30 border border-cyan-800/50 rounded-full px-2 py-0.5 text-cyan-300">
                                                    Payer
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-2 pl-2 border-l-2 border-cyan-800/30">
                                        <div className="flex items-center text-sm">
                                            <Phone className="w-4 h-4 mr-2 text-gray-500" />
                                            <span className="text-gray-400">Phone:</span>
                                            <span className="text-gray-300 ml-auto">{selectedOrder?.pherPhone}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Receiver Details */}
                                <div>
                                    <div className="flex items-center mb-3">
                                        <Avatar
                                            src={`${guessAdminRoute()}/assets/uploads/avatar/${selectedOrder?.gherImage}`}
                                            className="w-10 h-10 border-2 border-purple-600 mr-3"
                                            sx={{ bgcolor: "#9333ea" }}
                                        >
                                            {selectedOrder?.gherFirstname?.[0]}
                                            {selectedOrder?.gherSurname?.[0]}
                                        </Avatar>
                                        <div>
                                            <div className="text-white font-medium">
                                                {selectedOrder?.gherFirstname} {selectedOrder?.gherSurname}
                                            </div>
                                            <div className="text-xs text-gray-400 flex items-center">
                                                <User className="w-3 h-3 mr-1" />@{selectedOrder?.gherUsername}
                                                <span className="mx-2">•</span>
                                                <span className="bg-purple-900/30 border border-purple-800/50 rounded-full px-2 py-0.5 text-purple-300">
                                                    Earner
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-2 pl-2 border-l-2 border-purple-800/30">
                                        <div className="flex items-center text-sm">
                                            <Phone className="w-4 h-4 mr-2 text-gray-500" />
                                            <span className="text-gray-400">Phone:</span>
                                            <span className="text-gray-300 ml-auto">{selectedOrder?.gherPhone}</span>
                                        </div>

                                        {selectedOrder?.autoConfirm == 1 ? (
                                            <div className="flex items-center text-sm">
                                                <Wallet className="w-4 h-4 mr-2 text-gray-500" />
                                                <span className="text-gray-400">Wallet:</span>
                                                <Tooltip title={selectedOrder?.[`wallet${selectedOrder?.motherType}`]}>
                                                    <span className="text-gray-300 ml-auto truncate max-w-[200px]">
                                                        {selectedOrder?.[`wallet${selectedOrder?.motherType}`]}
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="flex items-center text-sm">
                                                    <Bank className="w-4 h-4 mr-2 text-gray-500" />
                                                    <span className="text-gray-400">Bank:</span>
                                                    <span className="text-gray-300 ml-auto">{selectedOrder?.gherBank}</span>
                                                </div>
                                                <div className="flex items-center text-sm">
                                                    <User className="w-4 h-4 mr-2 text-gray-500" />
                                                    <span className="text-gray-400">Account Name:</span>
                                                    <span className="text-gray-300 ml-auto">{selectedOrder?.gherAccountname}</span>
                                                </div>
                                                <div className="flex items-center text-sm">
                                                    <CreditCard className="w-4 h-4 mr-2 text-gray-500" />
                                                    <span className="text-gray-400">Account Number:</span>
                                                    <span className="text-gray-300 ml-auto">{selectedOrder?.gherAccountnumber}</span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Payment Proof */}
                            {selectedOrder?.image && selectedOrder?.image != "" && selectedOrder?.image != 0 && (
                                <div className="bg-gradient-to-r from-gray-900/40 to-gray-800/40 rounded-xl p-4 mb-6 border border-gray-700/30">
                                    <h3 className="text-lg font-semibold text-white mb-3">Payment Proof</h3>

                                    <div className="relative rounded-lg overflow-hidden border border-gray-700 mb-3">
                                        <img
                                            src={`${guessAdminRoute()}/assets/uploads/p2p/${selectedOrder?.image}`}
                                            alt="Payment Proof"
                                            className="w-full h-auto object-contain bg-black"
                                            style={{ maxHeight: "200px" }}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                        <Button
                                            variant="contained"
                                            startIcon={<Eye />}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                setFocusedPeer(selectedOrder)
                                            }}
                                            className="absolute bottom-2 right-2 bg-purple-600/80 hover:bg-purple-600 text-white text-xs py-1"
                                        >
                                            View Full Image
                                        </Button>
                                    </div>

                                    <div className="text-xs text-gray-400">
                                        Uploaded on {formatDateTime(Number(selectedOrder?.paymentTime))}
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="sticky bottom-0 pt-4 pb-2 bg-gray-900/80 backdrop-blur-sm">
                                {selectedOrder?.status !== "PAID" && (
                                    <div className="grid grid-cols-1 gap-3">
                                        {/* Payer Actions */}
                                        {selectedOrder?.pherAccountId === currentUserAccountId && selectedOrder?.status === "NO" && (
                                            <Button
                                                variant="contained"
                                                startIcon={uploadingProof ? <CircularProgress size={16} color="inherit" /> : <Upload />}
                                                disabled={uploadingProof}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleFileUpload(selectedOrder?.id)
                                                }}
                                                className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:shadow-cyan-500/20"
                                            >
                                                {uploadingProof ? "Uploading..." : "Upload Payment Proof"}
                                            </Button>
                                        )}

                                        {/* Receiver Actions */}
                                        {selectedOrder?.gherAccountId == currentUserAccountId && ['YES', 'NO'].includes(selectedOrder?.status) && (
                                            <div className="grid grid-cols-2 gap-3">
                                                <Button
                                                    variant="contained"
                                                    startIcon={confirmingProof ? <CircularProgress size={16} color="inherit" /> : <Check />}
                                                    disabled={confirmingProof}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        window.confirm("This action cannot be reversed. Click OK to confirm this transaction!") &&
                                                            handleMarkAsPaid(selectedOrder?.id)
                                                    }}
                                                    className="bg-gradient-to-r from-emerald-600 to-green-600 text-white hover:shadow-emerald-500/20"
                                                >
                                                    {confirmingProof ? "Processing..." : "Confirm Payment"}
                                                </Button>

                                                {selectedOrder?.reported == 0 && selectedOrder?.status == 'YES' && (
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={reportingProof ? <CircularProgress size={16} /> : <AlertTriangle />}
                                                        disabled={reportingProof}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            window.confirm("Are you sure you want to report this transaction?") &&
                                                                reportTransaction(selectedOrder?.id)
                                                        }}
                                                        className="border-rose-500 text-rose-500 hover:bg-rose-500/10"
                                                    >
                                                        {reportingProof ? "Reporting..." : "Report Issue"}
                                                    </Button>
                                                )}
                                            </div>
                                        )}

                                        <input
                                            type="file"
                                            id={`file-${selectedOrder?.id}`}
                                            className="hidden"
                                            onChange={(e) => handleFileChange(e, selectedOrder?.id)}
                                            accept="image/*"
                                        />
                                    </div>
                                )}
                            </div>
                        </Box>
                    </Box>
                )}
            </Drawer>

            {/* Image Popup */}
            <Dialog
                open={Boolean(focusedPeer)}
                onClose={() => setFocusedPeer(null)}
                maxWidth="lg"
                PaperProps={{
                    style: {
                        backgroundColor: "rgba(15, 23, 42, 0.95)",
                        backgroundImage: "linear-gradient(to bottom right, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))",
                        borderRadius: "0.75rem",
                        border: "1px solid rgba(76, 29, 149, 0.3)",
                        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
                    },
                }}
            >
                <DialogTitle className="border-b border-gray-800 flex justify-between items-center">
                    <Typography variant="h6" className="text-white font-bold">
                        Payment Proof
                    </Typography>
                    <IconButton onClick={() => setFocusedPeer(null)} className="text-gray-400 hover:text-white">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            className="w-6 h-6"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </IconButton>
                </DialogTitle>
                <DialogContent className="relative p-0">
                    {/* Glow effects */}
                    <div className="absolute top-20 left-20 w-40 h-40 bg-purple-500 rounded-full opacity-5 blur-3xl"></div>
                    <div className="absolute bottom-20 right-20 w-40 h-40 bg-cyan-500 rounded-full opacity-5 blur-3xl"></div>

                    <img
                        src={focusedPeer ? `${guessAdminRoute()}/assets/uploads/p2p/${focusedPeer?.image}` : ""}
                        alt="Payment Proof"
                        className="w-full h-auto max-h-[80vh] object-contain p-4"
                    />
                </DialogContent>
                <DialogActions className="border-t border-gray-800 p-3">
                    <Button
                        onClick={() => setFocusedPeer(null)}
                        className="bg-gradient-to-r from-gray-700 to-gray-800 text-white hover:shadow-gray-700/20"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    </BorderLine>
    )
}

export default P2POrderDisplay
